import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Copy, Mail, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { authUtils } from "@/utils/auth";
import { SessionChecklist } from "@/components/junior/SessionChecklist";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentCard = ({ s, pathway, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(s.junior_progress?.completed || []);
  const [sessions, setSessions] = useState(s.sessions || []);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const d = s.student || {};
  const pct = Math.round((done.length / pathway.length) * 100);

  const toggle = (id) => setDone(done.includes(id) ? done.filter((x) => x !== id) : [...done, id]);
  const save = async (notify) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/junior/students/${s.id}/progress`, { child: s.child, completed: done, note, notify_parent: notify, sessions: sessions.map(({ attended, feedback }) => ({ attended, feedback })) }, authUtils.getAuthConfig());
      setNote("");
      onSaved(s.key, r.data.progress);
      if (notify && !r.data.email_sent) toast.warning("Progress saved, but the parent email couldn't be sent. Copy the link and send it manually.");
      else toast.success(notify ? "Saved & parent emailed" : "Progress saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const copy = () => navigator.clipboard.writeText(s.progress_link).then(() => toast.success("Parent link copied"));

  return (
    <div className="glass rounded-2xl p-5" data-testid={`junior-student-${s.key}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 text-left" data-testid={`junior-student-toggle-${s.key}`}>
        <div>
          <p className="text-white font-semibold">{d.student_name} {s.is_sibling && <span className="ml-1 px-2 py-0.5 rounded-full bg-pink-500 text-white text-[10px] font-bold align-middle">SIBLING</span>} <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold align-middle ${s.service_id === "junior_dj_casual" ? "bg-cyan-300 text-black" : "bg-lime-300 text-black"}`} data-testid={`junior-student-package-${s.key}`}>{s.package}</span> <span className="text-white/40 text-sm">· age {d.student_age} · {d.experience}</span></p>
          <p className="text-white/50 text-xs mt-0.5">Parent: {s.parent_name} · {s.parent_mobile} · {s.email} · Starts {s.booking_date} · <span className="capitalize">{s.payment_status}</span></p>
          <p className="text-white/40 text-xs mt-0.5">{s.sessions.filter((x) => x.attended).length}/{s.sessions.length} sessions attended</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-28 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-lime-300" style={{ width: `${pct}%` }} /></div>
          <span className="text-lime-300 text-sm font-mono w-10 text-right" data-testid={`junior-student-pct-${s.key}`}>{pct}%</span>
        </div>
      </button>
      {open && (
        <div className="mt-5 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3 text-sm rounded-xl bg-white/[0.03] border border-white/10 p-4" data-testid={`junior-student-details-${s.key}`}>
            {[["Student", d.student_name], ["Age", d.student_age], ["Experience", d.experience], ["Genres", d.genres], ["Parent / guardian", s.parent_name], ["Parent mobile", s.parent_mobile], ["Email (updates sent here)", s.email], ["Booked by", s.booked_by === "student" ? "Student" : "Parent / guardian"], ["Package", s.package]].map(([l, v]) => (
              <div key={l}><p className="text-white/40 text-xs">{l}</p><p className="text-white break-words">{v || "—"}</p></div>
            ))}
          </div>
          <SessionChecklist sessions={sessions} setSessions={setSessions} testKey={s.key} student={s} />
          <p className="text-white/50 text-xs uppercase tracking-widest pt-2">Skills pathway</p>
          {[1, 2, 3, 4].map((w) => (
            <div key={w}>
              <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Week {w}</p>
              <div className="grid sm:grid-cols-3 gap-2">
                {pathway.filter((p) => p.week === w).map((p) => {
                  const on = done.includes(p.id);
                  return (
                    <button key={p.id} onClick={() => toggle(p.id)} className={`flex items-start gap-2 p-3 rounded-xl border text-left text-sm transition-colors ${on ? "bg-lime-300/15 border-lime-300/60 text-white" : "border-white/10 text-white/60 hover:border-white/30"}`} data-testid={`junior-step-${s.key}-${p.id}`}>
                      <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${on ? "bg-lime-300 text-black" : "border border-white/30"}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
                      {p.title}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Coach's note for the parent (optional), e.g. Nailed their first blend today!" className="bg-dream-surface border-dream-border text-white" data-testid={`junior-note-${s.key}`} />
          {s.junior_progress?.notes?.length > 0 && (
            <ul className="text-white/50 text-xs space-y-1">{s.junior_progress.notes.slice(-3).map((n) => <li key={n.created_at}>• {n.text} <span className="text-white/30">({new Date(n.created_at).toLocaleDateString()})</span></li>)}</ul>
          )}
          <div className="flex flex-wrap gap-2">
            <Button disabled={saving} onClick={() => save(true)} className="bg-lime-300 hover:bg-lime-200 text-black rounded-full" data-testid={`junior-save-notify-${s.key}`}><Mail className="w-4 h-4 mr-2" /> Save & email parent</Button>
            <Button disabled={saving} onClick={() => save(false)} variant="outline" className="border-white/20 text-white bg-transparent rounded-full" data-testid={`junior-save-${s.key}`}><Save className="w-4 h-4 mr-2" /> Save only</Button>
            <Button onClick={copy} variant="ghost" className="text-white/60 hover:text-white rounded-full" data-testid={`junior-copy-link-${s.key}`}><Copy className="w-4 h-4 mr-2" /> Copy parent link</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function JuniorProgressManager() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/junior/students`, authUtils.getAuthConfig()).then((r) => setData(r.data)).catch(() => setData({ students: [], pathway: [] }));
  }, []);
  const onSaved = (id, progress) => setData({ ...data, students: data.students.map((s) => (s.key === id ? { ...s, junior_progress: progress, sessions: s.sessions.map((x, i) => ({ ...x, ...(progress.sessions?.[i] || {}) })) } : s)) });

  if (!data) return <p className="text-white/50">Loading students…</p>;
  return (
    <div className="space-y-4" data-testid="junior-progress-manager">
      <div className="glass rounded-2xl p-6">
        <h2 className="font-heading text-xl font-bold text-white">Junior DJ Academy · Students, Sessions & Progress</h2>
        <p className="text-white/50 text-sm mt-1">Tick off each session, leave feedback and update skills. &quot;Save & email&quot; sends it to the email used at booking, and it&apos;s always on their private progress link.</p>
      </div>
      {data.students.length === 0 ? (
        <p className="text-white/50 text-sm" data-testid="junior-no-students">No Junior DJ Academy students yet.</p>
      ) : (
        data.students.map((s) => <StudentCard key={s.key} s={s} pathway={data.pathway} onSaved={onSaved} />)
      )}
      <VoucherList />
    </div>
  );
}

const VoucherList = () => {
  const [vouchers, setVouchers] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/vouchers`, authUtils.getAuthConfig()).then((r) => setVouchers(r.data.vouchers)).catch(() => setVouchers([]));
  }, []);
  if (!vouchers) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="admin-voucher-list">
      <h3 className="font-heading text-lg font-bold text-white mb-3">Gift Vouchers &amp; Gift Cards</h3>
      {vouchers.length === 0 ? <p className="text-white/50 text-sm">No vouchers sold yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-white/50"><tr><th className="py-2 pr-4">Code</th><th className="pr-4">Voucher</th><th className="pr-4">For</th><th className="pr-4">Bought by</th><th className="pr-4">Balance</th><th>Expires</th></tr></thead>
            <tbody className="text-white">
              {vouchers.map((v) => (
                <tr key={v.code} className="border-t border-white/10" data-testid={`admin-voucher-${v.code}`}>
                  <td className="py-2 pr-4 font-mono">{v.code}</td><td className="pr-4">{v.label}<span className="block text-white/40 text-xs" data-testid={`admin-voucher-scope-${v.code}`}>{v.scope === "all" ? (v.service_id ? "Studio · this service only" : "Studio · any service") : "Junior Academy only"}</span></td><td className="pr-4">{v.recipient_name}</td>
                  <td className="pr-4">{v.buyer_name}<span className="block text-white/40 text-xs">{v.buyer_email}</span></td>
                  <td className="pr-4">${v.balance.toFixed(2)} / ${v.value.toFixed(2)}</td><td>{new Date(v.expires_at).toLocaleDateString("en-AU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
